import React, { useEffect, useState } from 'react';
import HealthSherpa from './HealthSherpa';
import WriterDrawer from './WriterDrawer';
import HsAuthCodePanel from './HsAuthCodePanel';
import { postClientsChanged, requestOpenClient, MAIN_WINDOW_NAME, HEALTHSHERPA_WINDOW_NAME } from './insuredocsChannel';

// =============================================================================
// HealthSherpaApp (5.17.0) — the HealthSherpa page as its OWN browser tab (App renders it instead of
// Main when the address has ?healthsherpa=1, after the same login). It has its own Writer (top
// placement, own instance — drafts don't carry between tabs) and talks to an open InsureDocs tab over
// BroadcastChannel('insuredocs') — see insuredocsChannel.js. Only ids travel on the channel.
// =============================================================================

export default function HealthSherpaApp({ user }) {
  // Same source as Main: Main's dark mode is component state that starts dark (it isn't stored anywhere).
  const [darkMode] = useState(true);
  const [writerOpenRequest, setWriterOpenRequest] = useState(0);

  useEffect(() => {
    const prev = document.title;
    document.title = 'HealthSherpa — InsureDocs';
    try { if (!window.name) window.name = HEALTHSHERPA_WINDOW_NAME; } catch (_e) { /* ignore */ }
    return () => { document.title = prev; };
  }, []);

  // 5.27.4: this tab sends NO Referer (its address holds filters and the open person) — added on mount, removed on
  // unmount. The main InsureDocs app is not affected.
  useEffect(() => {
    let meta = null;
    try {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'referrer');
      meta.setAttribute('content', 'no-referrer');
      meta.setAttribute('data-hs-referrer', '1');
      document.head.appendChild(meta);
    } catch (_e) { meta = null; }
    return () => { try { if (meta && meta.parentNode) meta.parentNode.removeChild(meta); } catch (_e) { /* ignore */ } };
  }, []);

  // "Open client folder": ask an open InsureDocs tab first; no answer within ~800 ms -> open/reuse one.
  const openClientFolder = async (clientId) => {
    if (await requestOpenClient(clientId)) return 'tab';
    try { window.open(`/?client=${encodeURIComponent(clientId)}`, MAIN_WINDOW_NAME); } catch (_e) { /* popup blocked */ }
    return 'opened';
  };

  // A client created here, or a PDF saved into a client's folder: the InsureDocs tab refreshes.
  const clientsChanged = (clientIds) => { postClientsChanged(clientIds); };

  const close = () => {
    try { window.close(); } catch (_e) { /* not script-closable */ }
    // If the browser kept the tab open (it wasn't opened by script), go to normal InsureDocs.
    setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 300);
  };

  return (
    <>
      <HealthSherpa
        user={user}
        darkMode={darkMode}
        onClose={close}
        openClientFolder={openClientFolder}
        onClientTreesChanged={clientsChanged}
        onClientsChanged={clientsChanged}
        onOpenWriter={() => setWriterOpenRequest((n) => n + 1)}
      />
      <WriterDrawer darkMode={darkMode} placement="top" hideTab openRequest={writerOpenRequest} />
      {/* 5.41.0: live HealthSherpa / CMS verification codes (slides in from the right) */}
      <HsAuthCodePanel darkMode={darkMode} />
    </>
  );
}
